/**
 * Registry loader - loads rules from generated JSON files.
 */

import type { Rule, GlobalManifest, LanguageManifest, Language, Category } from './types';
import { getLanguageUIConfig, getCategoryUIConfig } from './ui-config';

// Import generated JSON files
import globalManifest from '@/public/registry/manifest.json';

/**
 * Lazy-loaded rule cache per language.
 */
const rulesCache: Record<string, Rule[]> = {};

/**
 * Lazy-loaded manifest cache per language.
 */
const manifestCache: Record<string, LanguageManifest> = {};

/**
 * Get global manifest.
 */
export function getGlobalManifest(): GlobalManifest {
  return globalManifest as GlobalManifest;
}

/**
 * Get manifest for a specific language.
 */
export function getLanguageManifest(languageId: string): LanguageManifest | undefined {
  if (manifestCache[languageId]) {
    return manifestCache[languageId];
  }

  try {
    const manifest = require(`@/public/registry/${languageId}/manifest.json`);
    manifestCache[languageId] = manifest as LanguageManifest;
    return manifestCache[languageId];
  } catch (error) {
    console.error(`Failed to load manifest for ${languageId}:`, error);
    return undefined;
  }
}

/**
 * Get all rules for a specific language.
 */
export function getRulesByLanguage(languageId: string): Rule[] {
  if (rulesCache[languageId]) {
    return rulesCache[languageId];
  }

  try {
    const rules = require(`@/public/registry/${languageId}/rules.json`);
    rulesCache[languageId] = rules as Rule[];
    return rulesCache[languageId];
  } catch (error) {
    console.error(`Failed to load rules for ${languageId}:`, error);
    return [];
  }
}

/**
 * Get all languages with UI configuration.
 */
export function getLanguages(): Language[] {
  const manifest = getGlobalManifest();
  const languages: Language[] = [];

  for (const languageId of manifest.languages) {
    const langManifest = getLanguageManifest(languageId);
    const uiConfig = getLanguageUIConfig(languageId);

    if (!langManifest) {
      continue;
    }

    languages.push({
      id: langManifest.id,
      name: langManifest.name,
      description: langManifest.description,
      ruleCount: langManifest.ruleCount,
      categoryCount: langManifest.categoryCount,
      icon: uiConfig.icon,
      color: uiConfig.color,
      command: `pathfinder scan --ruleset ${languageId}/all --project .`,
      status: uiConfig.status,
    });
  }

  return languages;
}

/**
 * Get a specific language by ID.
 */
export function getLanguage(languageId: string): Language | undefined {
  const languages = getLanguages();
  return languages.find(lang => lang.id === languageId);
}

/**
 * Get all categories for a specific language.
 */
export function getCategoriesByLanguage(languageId: string): Category[] {
  const manifest = getLanguageManifest(languageId);

  if (!manifest) {
    return [];
  }

  return manifest.categories.map(category => {
    const uiConfig = getCategoryUIConfig(category.id);

    return {
      id: category.id,
      name: category.name,
      description: uiConfig.description || `${category.name} rules for ${manifest.name}`,
      language: languageId,
      ruleCount: category.ruleCount,
      command: `pathfinder scan --ruleset ${languageId}/${category.id} --project .`,
      icon: uiConfig.icon,
    };
  });
}

/**
 * Get a specific category.
 */
export function getCategory(languageId: string, categoryId: string): Category | undefined {
  const categories = getCategoriesByLanguage(languageId);
  return categories.find(cat => cat.id === categoryId);
}

/**
 * Get rules by language and category.
 */
export function getRulesByCategory(languageId: string, categoryId: string): Rule[] {
  const rules = getRulesByLanguage(languageId);
  return rules.filter(rule => rule.category === categoryId);
}

/**
 * Get a specific rule by ID.
 */
export function getRule(languageId: string, categoryId: string, ruleId: string): Rule | undefined {
  const rules = getRulesByCategory(languageId, categoryId);
  return rules.find(rule => rule.id === ruleId);
}

/**
 * Get similar rules based on language or category.
 */
export function getSimilarRules(rule: Rule, limit: number = 3): Rule[] {
  // Use YAML-defined similar rules if available
  if (rule.similarRules && rule.similarRules.length > 0) {
    const allRules = getAllRules();
    const matched = rule.similarRules
      .map((id: string) => allRules.find(r => r.id === id))
      .filter((r: Rule | undefined): r is Rule => r !== undefined);
    if (matched.length > 0) return matched.slice(0, limit);
  }

  // Fallback: same category
  const allRules = getRulesByLanguage(rule.language);
  return allRules
    .filter(r => r.id !== rule.id && r.category === rule.category)
    .slice(0, limit);
}

/**
 * Get all rules across all languages.
 */
export function getAllRules(): Rule[] {
  const manifest = getGlobalManifest();
  const allRules: Rule[] = [];

  for (const languageId of manifest.languages) {
    allRules.push(...getRulesByLanguage(languageId));
  }

  return allRules;
}

/**
 * Get registry statistics.
 */
export function getRegistryStats() {
  const manifest = getGlobalManifest();

  const bySeverity: Record<string, number> = {
    CRITICAL: 0,
    HIGH: 0,
    MEDIUM: 0,
    LOW: 0,
    INFO: 0,
  };

  const byLanguage: Record<string, number> = {};

  for (const languageId of manifest.languages) {
    const rules = getRulesByLanguage(languageId);
    byLanguage[languageId] = rules.length;

    for (const rule of rules) {
      bySeverity[rule.severity] = (bySeverity[rule.severity] || 0) + 1;
    }
  }

  return {
    totalRules: manifest.total_rules,
    languages: manifest.languages.length,
    bySeverity,
    byLanguage,
    generatedAt: manifest.generated_at,
  };
}

/**
 * Backward compatibility: Export rules as a constant.
 * This loads all rules immediately and caches them.
 */
export const rules = getAllRules();
